import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { flowFetch, FlowArea, FlowUser, STATUS_LABELS, STATUS_COLORS } from './flowApi';
import { TaskDetailModal } from './FlowTasks';

type ViewMode = 'day' | 'week' | 'month' | 'list';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const HOUR_START = 6;   // primeira hora exibida na grade de dia/semana
const HOUR_END = 22;    // última hora exibida
const HOUR_H = 48;      // altura de cada hora (px)

// ---------- helpers de data ----------
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isToday = (d: Date) => isSameDay(d, new Date());
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const timeStr = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
// item é "sem horário" quando cai exatamente à meia-noite (data sem hora definida)
const hasTime = (d: Date) => d.getHours() !== 0 || d.getMinutes() !== 0;

function startOfWeek(d: Date) { return addDays(startOfDay(d), -d.getDay()); }

function monthGrid(base: Date): Date[] {
  const first = new Date(base.getFullYear(), base.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(gridStart, i)); // 6 semanas
  return cells;
}

function rangeFor(mode: ViewMode, base: Date): { start: Date; end: Date } {
  if (mode === 'month') {
    const cells = monthGrid(base);
    const start = cells[0];
    const end = new Date(cells[cells.length - 1]); end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (mode === 'week') {
    const start = startOfWeek(base);
    const end = new Date(addDays(start, 6)); end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (mode === 'list') {
    const start = startOfDay(base);
    const end = new Date(addDays(start, 30)); end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  const start = startOfDay(base);
  const end = new Date(base); end.setHours(23, 59, 59, 999);
  return { start, end };
}

// cor/estilo do "chip" conforme tipo
function itemChipClass(item: any) {
  if (item.kind === 'meeting') return item.status === 'CANCELLED' ? 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200' : item.delegated ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' : 'bg-amber-100 text-amber-800 hover:bg-amber-200';
  if (item.kind === 'event') return 'bg-violet-100 text-violet-800 hover:bg-violet-200';
  if (item.delegated) return 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200';
  if (item.status === 'DONE') return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200';
  return 'bg-slate-100 text-slate-700 hover:bg-slate-200';
}

export function FlowAgenda({ areas, users }: { areas: FlowArea[]; users: FlowUser[] }) {
  const [mode, setMode] = useState<ViewMode>('month');
  const [base, setBase] = useState(new Date());
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [meetingDetail, setMeetingDetail] = useState<any | null>(null);
  const [showEvent, setShowEvent] = useState(false);
  const location = useLocation();
  // /flow/agenda?d=2026-09-10 abre direto no dia (notificação de reunião)
  useEffect(() => {
    const d = new URLSearchParams(location.search).get('d');
    if (d) { const parsed = new Date(d); if (!Number.isNaN(parsed.getTime())) { setBase(parsed); setMode('day'); } }
  }, [location.search]);
  const [editingEventId, setEditingEventId] = useState<string | null>(null); // null = novo compromisso
  const [evTitle, setEvTitle] = useState('');
  const [evStart, setEvStart] = useState('');
  const [evDesc, setEvDesc] = useState('');
  const gridScrollRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { start, end } = rangeFor(mode, base);
      const data = await flowFetch(`/api/flow/agenda?start=${start.toISOString()}&end=${end.toISOString()}`);
      setItems(data.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar agenda');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [mode, base]);

  // rola a grade de dia/semana até ~7h ao montar
  useEffect(() => {
    if ((mode === 'day' || mode === 'week') && gridScrollRef.current) {
      gridScrollRef.current.scrollTop = Math.max(0, (8 - HOUR_START) * HOUR_H - 12);
    }
  }, [mode, loading]);

  const shift = (dir: number) => {
    if (mode === 'day' || mode === 'list') setBase(addDays(base, dir));
    if (mode === 'week') setBase(addDays(base, dir * 7));
    if (mode === 'month') setBase(new Date(base.getFullYear(), base.getMonth() + dir, 1));
  };

  // formata Date para datetime-local (yyyy-MM-ddTHH:mm) no fuso local
  const toLocalInput = (value: Date | string) => {
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openNewEvent = (date?: Date) => {
    setEditingEventId(null);
    setEvTitle(''); setEvDesc('');
    if (date) {
      const d = new Date(date);
      if (!hasTime(d)) d.setHours(9, 0, 0, 0);
      setEvStart(toLocalInput(d));
    } else {
      setEvStart('');
    }
    setShowEvent(true);
  };

  // Abre um compromisso existente para editar/excluir
  const openEditEvent = (item: any) => {
    setEditingEventId(item.id);
    setEvTitle(item.title || '');
    setEvDesc(item.description || '');
    setEvStart(item.at ? toLocalInput(item.at) : '');
    setShowEvent(true);
  };

  const closeEventModal = () => {
    setShowEvent(false);
    setEditingEventId(null);
    setEvTitle(''); setEvStart(''); setEvDesc('');
  };

  const saveEvent = async () => {
    if (!evTitle.trim() || !evStart) { toast.error('Informe título e horário'); return; }
    try {
      if (editingEventId) {
        await flowFetch(`/api/flow/events/${editingEventId}`, { method: 'PUT', body: JSON.stringify({ title: evTitle, startAt: evStart, description: evDesc }) });
        toast.success('Compromisso atualizado!');
      } else {
        await flowFetch('/api/flow/events', { method: 'POST', body: JSON.stringify({ title: evTitle, startAt: evStart, description: evDesc }) });
        toast.success('Compromisso criado!');
      }
      closeEventModal();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar compromisso');
    }
  };

  const deleteEvent = async (id: string) => {
    if (!confirm('Excluir este compromisso?')) return;
    try {
      await flowFetch(`/api/flow/events/${id}`, { method: 'DELETE' });
      closeEventModal();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const openItem = (item: any) => {
    if (item.kind === 'task') setDetailId(item.id);
    else if (item.kind === 'meeting') setMeetingDetail(item);
    else if (item.kind === 'event') openEditEvent(item);
  };

  // agrupa itens por dia
  const itemsByDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    items.forEach(item => {
      const d = new Date(item.at);
      (map[dayKey(d)] = map[dayKey(d)] || []).push(item);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()));
    return map;
  }, [items]);

  const headerLabel = () => {
    if (mode === 'month') return base.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    if (mode === 'week') {
      const s = startOfWeek(base); const e = addDays(s, 6);
      return `${s.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} – ${e.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`;
    }
    if (mode === 'day') return base.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
    return base.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      {/* Barra de controles */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white">
          {(['day', 'week', 'month', 'list'] as ViewMode[]).map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-xs font-semibold ${mode === m ? 'bg-cyan-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {{ day: 'Dia', week: 'Semana', month: 'Mês', list: 'Lista' }[m]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50">‹</button>
          <button onClick={() => setBase(new Date())} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Hoje</button>
          <button onClick={() => shift(1)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50">›</button>
        </div>
        <span className="text-sm font-bold capitalize text-slate-800">{headerLabel()}</span>
        <button onClick={() => openNewEvent()}
          className="ml-auto rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cyan-700">
          + Compromisso
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>
      ) : (
        <>
          {mode === 'month' && (
            <MonthView base={base} itemsByDay={itemsByDay} onItem={openItem} onDay={(d) => openNewEvent(d)} />
          )}
          {mode === 'week' && (
            <TimeGridView days={Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(base), i))}
              itemsByDay={itemsByDay} scrollRef={gridScrollRef} onItem={openItem} onSlot={openNewEvent} />
          )}
          {mode === 'day' && (
            <TimeGridView days={[startOfDay(base)]}
              itemsByDay={itemsByDay} scrollRef={gridScrollRef} onItem={openItem} onSlot={openNewEvent} />
          )}
          {mode === 'list' && (
            <ListView itemsByDay={itemsByDay} onItem={openItem} onDelete={deleteEvent} />
          )}
        </>
      )}

      {showEvent && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
          onMouseDown={e => { if (e.target === e.currentTarget) closeEventModal(); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-lg font-bold text-slate-900">{editingEventId ? 'Editar compromisso' : 'Novo compromisso'}</h2>
            <div className="space-y-3">
              <input autoFocus value={evTitle} onChange={e => setEvTitle(e.target.value)} placeholder="Ex.: Reunião com fornecedor"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <input type="datetime-local" value={evStart} onChange={e => setEvStart(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <textarea value={evDesc} onChange={e => setEvDesc(e.target.value)} placeholder="Detalhes (opcional)" rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              {editingEventId && (
                <button onClick={() => deleteEvent(editingEventId)}
                  className="mr-auto rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                  Excluir
                </button>
              )}
              <button onClick={closeEventModal} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700">Cancelar</button>
              <button onClick={saveEvent} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700">
                {editingEventId ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailId && <TaskDetailModal taskId={detailId} areas={areas} users={users} onClose={() => setDetailId(null)} onChanged={load} />}
      {meetingDetail && <MeetingDetailModal item={meetingDetail} onClose={() => setMeetingDetail(null)} onChanged={load} />}
    </div>
  );
}

// ============ Visão de Mês ============
function MonthView({ base, itemsByDay, onItem, onDay }: {
  base: Date; itemsByDay: Record<string, any[]>;
  onItem: (item: any) => void; onDay: (d: Date) => void;
}) {
  const cells = monthGrid(base);
  const currentMonth = base.getMonth();

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
      <div className="min-w-[560px]">
      {/* cabeçalho dos dias da semana */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map(w => (
          <div key={w} className="py-2 text-center text-xs font-bold uppercase tracking-wider text-slate-500">{w}</div>
        ))}
      </div>
      {/* grade */}
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const dayItems = itemsByDay[dayKey(day)] || [];
          const inMonth = day.getMonth() === currentMonth;
          const today = isToday(day);
          return (
            <div key={i}
              onClick={() => onDay(day)}
              className={`min-h-[104px] cursor-pointer border-b border-r border-slate-100 p-1.5 transition hover:bg-cyan-50/40 ${i % 7 === 0 ? 'border-l-0' : ''} ${inMonth ? 'bg-white' : 'bg-slate-50/60'}`}>
              <div className="mb-1 flex justify-end">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${today ? 'bg-cyan-600 text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'}`}>
                  {day.getDate()}
                </span>
              </div>
              <div className="space-y-1">
                {dayItems.slice(0, 3).map((item: any) => (
                  <button key={`${item.kind}-${item.id}`}
                    onClick={(e) => { e.stopPropagation(); onItem(item); }}
                    className={`flex w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium ${itemChipClass(item)}`}
                    title={item.title}>
                    {hasTime(new Date(item.at)) && <span className="shrink-0 tabular-nums opacity-70">{timeStr(new Date(item.at))}</span>}
                    <span className="truncate">{item.title}</span>
                  </button>
                ))}
                {dayItems.length > 3 && (
                  <p className="pl-1 text-[10px] font-semibold text-slate-400">+{dayItems.length - 3} mais</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>
      </div>
    </div>
  );
}

// ============ Visão de Dia/Semana (grade de horários) ============
function TimeGridView({ days, itemsByDay, scrollRef, onItem, onSlot }: {
  days: Date[]; itemsByDay: Record<string, any[]>; scrollRef: React.RefObject<HTMLDivElement>;
  onItem: (item: any) => void; onSlot: (d: Date) => void;
}) {
  const hours = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);
  const gridHeight = (HOUR_END - HOUR_START + 1) * HOUR_H;

  const split = (day: Date) => {
    const all = itemsByDay[dayKey(day)] || [];
    return {
      allDay: all.filter(i => !hasTime(new Date(i.at))),
      timed: all.filter(i => hasTime(new Date(i.at))),
    };
  };

  const posFor = (d: Date) => {
    const minutes = (d.getHours() - HOUR_START) * 60 + d.getMinutes();
    return Math.max(0, (minutes / 60) * HOUR_H);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* cabeçalho dos dias */}
      <div className="flex border-b border-slate-200 bg-slate-50">
        <div className="w-14 shrink-0" />
        {days.map((day, i) => (
          <div key={i} className="flex-1 border-l border-slate-100 py-2 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{WEEKDAYS[day.getDay()]}</p>
            <p className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold ${isToday(day) ? 'bg-cyan-600 text-white' : 'text-slate-700'}`}>
              {day.getDate()}
            </p>
          </div>
        ))}
      </div>

      {/* faixa "sem horário" */}
      {days.some(d => split(d).allDay.length > 0) && (
        <div className="flex border-b border-slate-200 bg-slate-50/50">
          <div className="flex w-14 shrink-0 items-center justify-end pr-2 text-[10px] font-semibold text-slate-400">dia todo</div>
          {days.map((day, i) => (
            <div key={i} className="flex-1 space-y-1 border-l border-slate-100 p-1">
              {split(day).allDay.map((item: any) => (
                <button key={`${item.kind}-${item.id}`} onClick={() => onItem(item)}
                  className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium ${itemChipClass(item)}`}
                  title={item.title}>{item.title}</button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* grade de horas */}
      <div ref={scrollRef} className="max-h-[560px] overflow-y-auto">
        <div className="flex" style={{ height: gridHeight }}>
          {/* coluna de horários */}
          <div className="w-14 shrink-0">
            {hours.map(h => (
              <div key={h} className="relative" style={{ height: HOUR_H }}>
                <span className="absolute -top-2 right-2 text-[10px] font-semibold text-slate-400">{String(h).padStart(2, '0')}h</span>
              </div>
            ))}
          </div>
          {/* colunas dos dias */}
          {days.map((day, di) => {
            const { timed } = split(day);
            return (
              <div key={di} className="relative flex-1 border-l border-slate-100">
                {/* linhas de hora + clique para criar */}
                {hours.map(h => (
                  <div key={h}
                    onClick={() => { const d = new Date(day); d.setHours(h, 0, 0, 0); onSlot(d); }}
                    className="cursor-pointer border-b border-slate-100 hover:bg-cyan-50/40" style={{ height: HOUR_H }} />
                ))}
                {/* linha do "agora" */}
                {isToday(day) && (() => {
                  const now = new Date();
                  if (now.getHours() < HOUR_START || now.getHours() > HOUR_END) return null;
                  return <div className="pointer-events-none absolute left-0 right-0 z-10 border-t-2 border-red-500" style={{ top: posFor(now) }}>
                    <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                  </div>;
                })()}
                {/* eventos posicionados */}
                {timed.map((item: any) => {
                  const d = new Date(item.at);
                  return (
                    <button key={`${item.kind}-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); onItem(item); }}
                      className={`absolute left-1 right-1 z-20 overflow-hidden rounded-md border-l-2 px-1.5 py-1 text-left shadow-sm ${itemChipClass(item)} ${item.kind === 'meeting' ? 'border-amber-500' : item.kind === 'event' ? 'border-violet-500' : item.delegated ? 'border-cyan-500' : 'border-slate-400'}`}
                      style={{ top: posFor(d), minHeight: 22 }}
                      title={item.title}>
                      <span className="block text-[10px] font-bold tabular-nums opacity-70">{timeStr(d)}</span>
                      <span className="block truncate text-[11px] font-semibold leading-tight">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============ Visão de Lista ============
function ListView({ itemsByDay, onItem, onDelete }: {
  itemsByDay: Record<string, any[]>; onItem: (item: any) => void; onDelete: (id: string) => void;
}) {
  const keys = Object.keys(itemsByDay).sort((a, b) => {
    const first = (k: string) => new Date(itemsByDay[k][0].at).getTime();
    return first(a) - first(b);
  });
  if (keys.length === 0) return <p className="py-12 text-center text-slate-400">Nada na agenda para este período.</p>;

  return (
    <div className="space-y-5">
      {keys.map(k => {
        const dayItems = itemsByDay[k];
        const label = new Date(dayItems[0].at).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
        return (
          <div key={k}>
            <p className="mb-2 text-sm font-bold capitalize text-slate-700">{label}</p>
            <div className="space-y-1.5">
              {dayItems.map((item: any) => (
                <div key={`${item.kind}-${item.id}`}
                  onClick={() => onItem(item)}
                  className={`flex items-center gap-3 rounded-xl border bg-white px-4 py-2.5 shadow-sm ${item.kind === 'task' ? 'cursor-pointer border-slate-200 hover:border-cyan-300' : item.kind === 'meeting' ? 'cursor-pointer border-amber-200 hover:border-amber-400' : 'cursor-pointer border-violet-200'}`}>
                  <span className="w-14 shrink-0 text-xs font-bold text-slate-500">
                    {hasTime(new Date(item.at)) ? timeStr(new Date(item.at)) : 'dia todo'}
                  </span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${item.kind === 'meeting' ? 'bg-amber-100 text-amber-800' : item.kind === 'event' ? 'bg-violet-100 text-violet-700' : item.delegated ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'}`}>
                    {item.kind === 'meeting' ? (item.delegated ? 'Reunião (agendei)' : 'Reunião') : item.kind === 'event' ? 'Compromisso' : item.delegated ? 'Delegada' : 'Tarefa'}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{item.title}{item.kind === 'meeting' && item.contactName ? <span className="text-slate-500"> · {item.contactName}</span> : null}</span>
                  {item.kind === 'meeting' && item.delegated && item.user && <span className="text-xs text-slate-500">{item.user.nome}</span>}
                  {item.kind === 'meeting' && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${MEETING_STATUS_COLORS[item.status] || 'bg-slate-100'}`}>{MEETING_STATUS_LABELS[item.status] || item.status}</span>}
                  {item.kind === 'task' && item.delegated && item.assignee && (
                    <span className="text-xs text-slate-500">{item.assignee.nome}</span>
                  )}
                  {item.kind === 'task' && (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[item.status]}`}>{STATUS_LABELS[item.status]}</span>
                  )}
                  {item.kind === 'event' && (
                    <button onClick={e => { e.stopPropagation(); onDelete(item.id); }} className="text-xs text-slate-300 hover:text-red-500">Excluir</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============ Detalhe da reunião (agendada pelo Chat CRM) ============
const MEETING_STATUS_LABELS: Record<string, string> = { SCHEDULED: 'Agendada', CONFIRMED: 'Confirmada', DONE: 'Realizada', CANCELLED: 'Cancelada', NO_SHOW: 'Não compareceu' };
const MEETING_STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-amber-100 text-amber-800', CONFIRMED: 'bg-emerald-100 text-emerald-800', DONE: 'bg-slate-200 text-slate-700', CANCELLED: 'bg-red-100 text-red-700', NO_SHOW: 'bg-rose-100 text-rose-700'
};
const MEETING_MODE_LABELS: Record<string, string> = { ONLINE: 'Online', PRESENCIAL: 'Presencial', TELEFONE: 'Telefone' };

function MeetingDetailModal({ item, onClose, onChanged }: { item: any; onClose: () => void; onChanged: () => void }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(item.title || '');
  const [desc, setDesc] = useState(item.description || '');
  const [location, setLocation] = useState(item.location || '');
  const [mode, setMode] = useState(item.meetingMode || 'ONLINE');
  const toLocal = (value: string) => { const d = new Date(value); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
  const [start, setStart] = useState(item.at ? toLocal(item.at) : '');
  const durationMs = item.endAt ? new Date(item.endAt).getTime() - new Date(item.at).getTime() : 60 * 60000;
  const reminder = item.metadata?.reminder || null;

  const patch = async (body: any, okMsg: string) => {
    setSaving(true);
    try {
      await flowFetch(`/api/meetings/${item.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      toast.success(okMsg);
      onChanged();
      onClose();
    } catch (err: any) {
      if (err?.message?.includes('compromisso(s) neste horário') && confirm(`${err.message}\nRemarcar mesmo assim?`)) {
        try { await flowFetch(`/api/meetings/${item.id}`, { method: 'PATCH', body: JSON.stringify({ ...body, ignoreConflicts: true }) }); toast.success(okMsg); onChanged(); onClose(); }
        catch (e2) { toast.error(e2 instanceof Error ? e2.message : 'Erro ao salvar'); }
      } else {
        toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
      }
    } finally { setSaving(false); }
  };

  const save = () => {
    if (!title.trim() || !start) { toast.error('Informe título e horário'); return; }
    const startAt = new Date(start);
    patch({ title, description: desc, location, meetingMode: mode, startAt: startAt.toISOString(), endAt: new Date(startAt.getTime() + durationMs).toISOString() }, 'Reunião atualizada!');
  };
  const setStatus = (status: string) => {
    if (status === 'CANCELLED' && !confirm('Cancelar esta reunião? O lembrete ao contato (se houver) também será cancelado.')) return;
    patch({ status }, status === 'CANCELLED' ? 'Reunião cancelada' : 'Status atualizado');
  };
  const remove = async () => {
    if (!confirm('Excluir esta reunião da agenda? O lembrete ao contato será cancelado.')) return;
    setSaving(true);
    try { await flowFetch(`/api/flow/events/${item.id}`, { method: 'DELETE' }); toast.success('Reunião excluída'); onChanged(); onClose(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao excluir'); }
    finally { setSaving(false); }
  };

  const at = new Date(item.at);
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${MEETING_STATUS_COLORS[item.status] || 'bg-slate-100'}`}>{MEETING_STATUS_LABELS[item.status] || item.status}</span>
            <h2 className="mt-1 text-lg font-bold text-slate-900">📅 {item.title}</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">✕</button>
        </div>

        {!editing ? (
          <div className="space-y-2 text-sm text-slate-700">
            <p className="capitalize"><b>{at.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</b> · {timeStr(at)}{item.endAt ? `–${timeStr(new Date(item.endAt))}` : ''}</p>
            {item.contactName && <p>Contato: <b>{item.contactName}</b>{item.contactPhone ? ` · ${item.contactPhone}` : ''}</p>}
            <p>Responsável: <b>{item.user?.nome || '—'}</b>{item.createdBy && item.createdBy.id !== item.userId ? <span className="text-slate-500"> · agendada por {item.createdBy.nome}</span> : null}</p>
            <p>{MEETING_MODE_LABELS[item.meetingMode] || 'Reunião'}{item.location ? <> · {/^https?:\/\//i.test(item.location) ? <a href={item.location} target="_blank" rel="noreferrer" className="text-cyan-700 underline">{item.location}</a> : item.location}</> : null}</p>
            {item.description && <p className="whitespace-pre-wrap rounded-lg bg-slate-50 px-3 py-2 text-slate-600">{item.description}</p>}
            <p className="text-xs text-slate-500">
              {reminder ? `Lembrete ao contato programado (${reminder.kind === 'template' ? 'template oficial' : 'mensagem'}) para ${new Date(reminder.scheduledFor).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.` : 'Sem lembrete ao contato.'}
              {item.metadata?.reminderWarning ? ` ${item.metadata.reminderWarning}` : ''}
            </p>
            {item.conversationId && (
              <button onClick={() => navigate('/chat-crm', { state: { selectConversationId: item.conversationId } })} className="text-xs font-semibold text-cyan-700 hover:underline">Abrir conversa no Chat CRM →</button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Título" />
            <input type="datetime-local" value={start} onChange={e => setStart(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="flex gap-2">
              {Object.entries(MEETING_MODE_LABELS).map(([value, label]) => (
                <button key={value} type="button" onClick={() => setMode(value)} className={`flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold ${mode === value ? 'border-cyan-600 bg-cyan-600 text-white' : 'border-slate-300 text-slate-600'}`}>{label}</button>
              ))}
            </div>
            <input value={location} onChange={e => setLocation(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Link, endereço ou telefone" />
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Descrição / pauta" />
            <p className="text-[11px] text-slate-500">Ao remarcar, o lembrete ao contato acompanha o novo horário.</p>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!editing ? (
            <>
              {item.status !== 'CANCELLED' && item.status !== 'DONE' && (
                <>
                  {item.status !== 'CONFIRMED' && <button disabled={saving} onClick={() => setStatus('CONFIRMED')} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Confirmar</button>}
                  <button disabled={saving} onClick={() => setStatus('DONE')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Realizada</button>
                  <button disabled={saving} onClick={() => setStatus('NO_SHOW')} className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">Não compareceu</button>
                  <button disabled={saving} onClick={() => setStatus('CANCELLED')} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">Cancelar reunião</button>
                </>
              )}
              {item.status === 'CANCELLED' && <button disabled={saving} onClick={() => setStatus('SCHEDULED')} className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50">Reabrir</button>}
              <span className="ml-auto flex gap-2">
                <button disabled={saving} onClick={remove} className="rounded-lg px-3 py-1.5 text-xs text-slate-400 hover:text-red-600">Excluir</button>
                <button onClick={() => setEditing(true)} className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700">Editar / remarcar</button>
              </span>
            </>
          ) : (
            <span className="ml-auto flex gap-2">
              <button onClick={() => setEditing(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700">Voltar</button>
              <button disabled={saving} onClick={save} className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700 disabled:opacity-50">{saving ? 'Salvando…' : 'Salvar'}</button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
